import { Style } from './style';

//-----------------------------------------------------------------------------
// auth
//-----------------------------------------------------------------------------
export interface AuthUser {
	id: string;
	name: string;
	email: string;
	permissions: string[];
	inviteCodes?: string[];
}

//-----------------------------------------------------------------------------
// user
//-----------------------------------------------------------------------------
export enum Tier {
	FREE = 0,
	TIER_1 = 1,
	TIER_2 = 2,
}

export interface Subscription {
	userId: string;
	tier: Tier;
	stripeSubscriptionId?: string;
	createdAt: Date;
	updatedAt: Date;
	amount?: number;
	interval?: 'month' | 'year';
	currentPeriodEnd?: Date;
}

export interface User {
	id: string;
	displayName?: string;
	metadata: UserMetadata;
	stripeCustomerId?: string;
	createdAt: Date;
	writeId: string;
}

export type UserMetadata = {
	completedLearningCenterVideos?: LearningCenterVideo[];
	numberOfExports?: number;
	lastExportDate?: string;
	numberOfEdits?: number;
	lastEditDate?: string;
	lastSharedDate?: string;
	lastSceneId?: string;
	// number of times the user has gotten a free gold refill (undefined if no refills have been done yet).
	// we are running this feature as we launch monetization. we intend to remove it after we transition
	// (perhaps we will build in another kind of refill but we'll probably handle it differently)
	bonusRefillCount?: number;
};

export type LearningCenterVideo = 'intro' | 'design_system' | 'canvas' | 'export';

//-----------------------------------------------------------------------------
// scene
//-----------------------------------------------------------------------------
export type PartialScene = Partial<Scene> & { id: string };

export interface Scene {
	id: string;
	title: string;
	metadata: SceneMetadata;
	createdAt: Date;
	writeId: string;
	viewOnly: boolean;
}
export type SceneMetadata = {
	canvasColor: string;
	canvasDropShadowMode: DropShadowMode;
	devices: Device[];
	currentStyle: number | null;
	styles: Style[];
	pendingEmails?: string[];
	// store timestamps at which the user opened the scene, keyed by userId
	openActions?: Record<string, Date>;
	publiclyReadable?: boolean;
	xConversationId?: string; // the id of a conversation on X.com
	xReplyConversationId?: string; // the id of a conversation we replied to on X.com
};

export enum DropShadowMode {
	NONE = 'none',
	LIGHT = 'light',
	MEDIUM = 'medium',
	HEAVY = 'heavy',
}

//-----------------------------------------------------------------------------
// frame
//-----------------------------------------------------------------------------
export type PartialFrame = Partial<Frame> & { id: string };
// gap between frames, in pixels
export const FRAME_GAP = 50;

export const CHAT_COLUMN_GAP = 200;

export interface Frame {
	id: string;
	sceneId: string;
	title: string;
	x: number;
	y: number;
	width: number;
	height: number;
	imagePath?: string; // Deprecated field. Set to undefined. Now forge has a dedicated route /frame/${frame.id}/image that returns the frame's image
	siteId?: string;
	entries?: Array<FrameEntry>;
	metadata: FrameMetadata;
	createdAt: Date;
	deletedAt: Date | null;
	writeId: string;
	viewOnly: boolean;
}
export type FrameMetadata = {
	type: FrameType;
	status: FrameStatus;
	statusMessage: string;
	displayLabels?: Record<string, string>;
	sourceFrameIds?: string[];
	sourceChatId?: string;
	// generationOptions <-- we no longer use this field. Leaving it here commented out to prevent anyone from making another generationOptions (which would pull the old data from legacy frames - potentially leading to mayhem)
	backgroundColor?: string;
	publiclyReadable?: boolean; // if true, anyone can view this frame without needing access to the scene/etc
};

export enum FrameType {
	GENERATE = 'generate',
	GENERATE_FROM_CHAT = 'generate-from-chat',
	ENHANCE = 'enhance',
	ADJUST = 'adjust',
	IMPORT = 'import',
	MERGE = 'merge',
	PROTOTYPE = 'prototype',
	IMPORT_SITE = 'import-site',
	// For testing and development purposes our systems will create frames from time to time.
	// These frames may not be associated with any of the user-facing flavors defined above.
	// So we use this type to keep track of them.
	SYSTEM = 'system',
}

export enum FrameStatus {
	PROCESSING = 'processing',
	ERROR = 'error',
	READY = 'ready',
}

//-----------------------------
// frame entries
export interface BaseFrameEntry {
	id: string;
	kind: string;
	createdAt: Date;
	deletedAt: Date | null;
}

export interface CommentFrameEntry extends BaseFrameEntry {
	kind: 'comment';
	message: string;
	userId: string;
}

export interface EditFrameEntry extends BaseFrameEntry {
	kind: 'edit';
	prompt: string;
	selectedDomElements: DomElement[];
}

export interface SiteFrameEntry extends BaseFrameEntry {
	kind: 'site';
	siteId: string;
	label?: string; // e.g. "Version 1"
	subLabel?: string; // e.g. "branched from Version 3"
}

export interface RecipeFrameEntry extends BaseFrameEntry {
	kind: 'recipe';
	// Recipe data needed to recreate GenerateSiteCommand for retry
	prompt: string;
	device: Device;
	codeToInclude?: string;
	referenceFrameIds: string[];
	chatEntries: ChatEntry[];
	style: string | null;
	wireframe: boolean;
	logPrefix: string;
	// for now, not including flowReferenceHtml. if we find we want better quality on retried flow frames we can extend this feature to include it
	// flowReferenceHtml?: string;
	statusString?: string;
	quality?: Quality;
}

export type FrameEntry = CommentFrameEntry | SiteFrameEntry | EditFrameEntry | RecipeFrameEntry;

//-----------------------------------------------------------------------------
// chat
//-----------------------------------------------------------------------------
export type PartialChat = Partial<Chat> & { id: string };

export interface Chat {
	id: string;
	sceneId: string;
	title: string;
	x: number;
	y: number;
	entries: Array<ChatEntry>;
	metadata: ChatMetadata;
	createdAt: Date;
	updatedAt: Date;
	writeId: string;
}

export type ChatEntry = SystemChatEntry | UserChatEntry;
export type TweetAuthor = 'original' | 'mention' | 'moonchild';
export interface SystemChatEntry {
	kind: 'system';
	message: string;
	restriction?: ChatEntryRestriction;
	tweetAuthor?: TweetAuthor;
}
export interface UserChatEntry {
	kind: 'user';
	message: string;
	sourceFrameIds: string[];
	attachments?: Attachment[];
	tweetAuthor?: TweetAuthor;
}

export interface Attachment {
	filename: string;
	contentType: string;
	storagePath: string;
	size: number;
}

export type ChatMetadata = {
	status: ChatStatus;
	statusMessage: string;
};

export enum ChatStatus {
	PROCESSING = 'processing',
	STOPPABLE = 'stoppable',
	ERROR = 'error',
	READY = 'ready',
}

export enum ChatEntryRestriction {
	USER = 'user', // show to the user in studio, but do not provide to the LLM
	LLM = 'llm', // show to the llm as part of the chat, but do not show to the user in studios
}

export enum ChatMode {
	DESIGN_AND_CHAT = 'design-and-chat', // Generates designs and responds with conversation
	// DESIGN_ONLY = 'design-only', // Only makes designs
	CHAT_ONLY = 'chat-only', // Only chats
}

export enum Quality {
	GOLD = 'gold',
	SILVER = 'silver',
}

//-----------------------------------------------------------------------------
// device
//-----------------------------------------------------------------------------

// should we update frame to use this instead of x/y? this feels like it belongs
// as part of the Frame interface - ss
export interface Device {
	type: DeviceType | string; // e.g. "mobile", "tablet, "iPhone 16e", "80s console game"
	width: number;
	height: number;
}

export enum DeviceType {
	MOBILE = 'mobile',
	TABLET = 'tablet',
	DESKTOP = 'desktop',
	CUSTOM = 'custom',
}

export const DEFAULT_DEVICES: Device[] = [
	{ type: 'mobile', width: 430, height: 932 },
	{ type: 'tablet', width: 768, height: 1024 },
	{ type: 'desktop', width: 1728, height: 1117 },
];

//-----------------------------------------------------------------------------
// site
//-----------------------------------------------------------------------------
export type SiteMap = Record<string, string>;

export interface Site {
	id: string;
	sceneId: string;
	files: SiteMap;
}

export type Moonshot = {
	code: string;
	title: string;
	viewport: { width: number; height: number };
	fullPage: boolean;
	siteMap: SiteMap;
	url: string;
};

//-----------------------------------------------------------------------------
// invite code
//-----------------------------------------------------------------------------
export interface InviteCode {
	id: string;
	code: string;
	userId: string;
	claimedUserIds: string[];
	count: number;
	createdAt: Date;
}

export type PartialInviteCode = Partial<InviteCode> & { id: string };

//-----------------------------------------------------------------------------
// google fonts
//-----------------------------------------------------------------------------
export interface GoogleFont {
	family: string;
	variants: string[];
	subsets: string[];
	version: string;
	lastModified: string;
	files: Record<string, string>;
	category: string;
	kind: string;
}

//-----------------------------------------------------------------------------
// site-related stuff
//-----------------------------------------------------------------------------
export interface DomElement {
	id: string;
	tagName: string;
	className: string;
}

//-----------------------------------------------------------------------------
// devigner message types
//-----------------------------------------------------------------------------
export interface SelectionInfo {
	tagName: string;
	id: string;
	className: string;
	// dataset is a DOMStringMap, which forge can't import directly. So use the underlying Record <string, string | undefined> instead
	dataset: Record<string, string | undefined>;
	bounds: {
		x: number;
		y: number;
		width: number;
		height: number;
	};
	textContent?: string;
}

// Studio → Devigner (commands)
export type ToDevigner = (
	| { type: 'startOverlay' }
	| { type: 'stopOverlay' }
	| { type: 'getSelectionSvg'; selectionInfo: SelectionInfo }
) & { siteId: string };

// Devigner → Studio (events)
export type ToStudio = (
	| { type: 'devignerReady' }
	| { type: 'select'; id: string; tagName: string; className: string }
	| {
			type: 'keypress';
			selectedElementId?: string;
			key: string;
			code: string;
			ctrlKey: boolean;
			altKey: boolean;
			shiftKey: boolean;
			metaKey: boolean;
	  }
	| { type: 'contextmenu'; id?: string; point: { x: number; y: number }; selectionInfo: string }
	| { type: 'ready' }
	| { type: 'svg'; svg: string }
	| {
			type: 'elementSvg';
			svg?: string;
			bounds?: { width: number; height: number };
			error?: string;
	  }
) & { siteId: string };

//-----------------------------------------------------------------------------
// site-related stuff
//-----------------------------------------------------------------------------

export interface TweetAttachment {
	url: string;
	type: 'photo';
	media_key: string;
	width: number;
	height: number;
}

export interface Tweet {
	authorId?: string;
	authorUsername: string;
	authorDescription: string;
	authorIsVerified: boolean;
	conversationId: string;
	conversationText: string;
	conversationDate: string;
	attachments?: TweetAttachment[];
	originalTweet?: Tweet;
	thread?: Tweet[];
}
